/* Copyright 2015-2018 The MathWorks, Inc. */
define([
  'dojo/io-query',
  'mw-html-utils/_parseHtml/sanitize',
  'mw-deprecation-utils/deprecation'
], function (ioQuery, sanitize, deprecation) {
  return {

    HYPERLINK_ESCAPE_REGEXP: /<a\s+href\s*=\s*"[^"]*"[^>]*>[\s\S]*?<\/a>/g,
    HYPERLINK_WITH_PARENTHESIS_ESCAPE_REGEXP:
            /<a\s+href\s*=\s*"([^"]*)"([^>]*)>([\s\S]*?)<\/a>/g,
    STRONG_ESCAPE_REGEXP: /<strong>|<\/strong>/g,
    BOLD_LINK_ATTRIBUTE_REGEX: /\bstyle\s*=\s*"[^"]*\bfont-weight\s*:\s*bold\b[^"]*"/,

    /**
     * Parses query string and returns an object with each name value pair on it. This routine
     * will also decode query values that have been encoded with %bytes per the contract of
     * [dojo/io].queryToObject
     */
    parseQueryString: function () {
      var queryString = window.location.search.substr(1,
        window.location.search.length);

      return this._parseQueryString(queryString);
    },

    /**
     * Utility method with explicit query string parsing functionality, broken out for testing
     * purposes.
     *
     * @private
     */
    _parseQueryString: function (queryString) {
      return ioQuery.queryToObject(queryString);
    },

    /**
     * Parses url into parts
     *
     * @param url {string} valid url, if URL is empty will implicitly use window.location
     * @returns {object} with protocol, hostname, port, pathname, search, and hash.
     */
    parseUrl: function (url) {
      // Switch to URL Object when tech stablizes.
      // https://developer.mozilla.org/en-US/docs/Web/API/URL
      var a = document.createElement('a');
      a.display = 'none';
      a.href = url || window.location.href;
      a.href = a.href; // IE will expand any relative hrefs when calling the href "getter"
      // Therefore this reassigns the href with the fully qualified version
      var parsedUrl = {
        protocol: a.protocol,
        hostname: a.hostname,
        port: a.port,
        pathname: a.pathname,
        search: a.search,
        hash: a.hash
      };

      if ((parsedUrl.protocol === 'https:' || parsedUrl.protocol === 'http:') &&
                parsedUrl.pathname.indexOf('/') !== 0) {
        // IE does not include "/" in start of pathname, normalize to other browsers
        parsedUrl.pathname = '/' + parsedUrl.pathname;
      }
      if (parsedUrl.protocol === 'https:' && parsedUrl.port === '443' &&
                url.indexOf('https://' + parsedUrl.hostname + ':443') !== 0) {
        // IE returns 443 as port when using HTTPS even if port
        // was not specified in URL, normalize to other browsers
        parsedUrl.port = '';
      }
      return parsedUrl;
    },

    /**
     * Translates the given string into one where all required HTML entity characters have
     * been escaped. E.g., "this > that" becomes "this &gt; that".<br /><br />
     *
     * Leaves chars alone on the assumption the browser is able to display them
     * properly already - since the JS string should contain valid code points.
     * Any data should have come via a UTF-8 encoded string or pasted and processed
     * into a valid JavaScript string representation.<br /><br />
     *
     * There was code originally to escape certain code points > 128 (g1103331)
     * But that has been removed per g1777495<br /><br />
     *
     * @param {string} string - the string to escape
     * @returns {string} as HTML with appropriate entities escaped.
     */
    escapeHtml: function (string) {
      let div = document.createElement('div');
      div.textContent = string;

      let newString = div.innerHTML;
      return newString;
    },

    /**
     * This function prevents the links from being escaped out.
     * Use treatHtmlLikeCommandWindow instead.
     * @deprecated @see {@link treatHtmlLikeCommandWindow}
     * @param {string} string - the string to perform escaping on
     * @param {boolean} escapeLinks - if this parameter is set to false, this function prevents
     * escaping the links.
     * @returns {string} a new string after escaping html and without escaping links
     * and strong tags
     */
    escapeLinksAndHtml: function (string, escapeLinks) {
      deprecation.deprecated({
        deprecated: {
          module: 'mw-html-utils/HtmlUtils',
          method: 'escapeLinksAndHtml'
        },
        replacement: {
          module: 'mw-html-utils/HtmlUtils',
          method: 'treatHtmlLikeCommandWindow'
        }});
      var newString = '';
      var normalStrings = string.split(this.HYPERLINK_ESCAPE_REGEXP);
      var aTags = string.match(this.HYPERLINK_ESCAPE_REGEXP);
      var newHyperLink = '';
      var that = this;
      var i = 0;
      var regexpReplaceFunction = function (match, linkUrl, remainder, linkLabel) {
        var protocol = that.parseUrl(linkUrl).protocol;
        // Only allow linkUrl that has designated protocol,
        // otherwise replace the url with "#"
        if (protocol !== 'http:' && protocol !== 'https:' && protocol !== 'matlab:') {
          linkUrl = '#';
        }
        linkUrl = that.escapeHtml(linkUrl);
        linkLabel = that._strongNotEscaped(linkLabel, true);
        newHyperLink = '<a href="' + linkUrl + '">' + linkLabel + '</a>';
      };

      if (!escapeLinks) {
        if (normalStrings[0] !== '') {
          newString = this._strongNotEscaped(normalStrings[0], true);
        }

        if (aTags !== null && aTags.length !== 0) {
          for (i = 0; i < aTags.length; i = i + 1) {
            aTags[i].replace(this.HYPERLINK_WITH_PARENTHESIS_ESCAPE_REGEXP,
              regexpReplaceFunction);
            if (normalStrings[i + 1] !== '') {
              normalStrings[i + 1] = this._strongNotEscaped(normalStrings[i + 1],
                true);
            }

            newString += newHyperLink + normalStrings[i + 1];
          }
        }
      } else {
        newString = this.escapeHtml(string);
        newString = this.escapeWhitespace(newString);
      }

      return newString;
    },

    /**
     * This function treats HTML like the MATLAB Command window
     * It safely escapes HTML to avoid XSS, but still allows links
     * (if escape links is true), and still allows strong tags.
     *
     * @param {string} string - the string to perform escaping on
     * @param {boolean} escapeLinks - true if you want this function to escape any links,
     *                  false if you want the links to stay.  It will still filter out unsafe
     *                  links/attributes.
     * @param {object} options - options parameter
     * @param {boolean} options.addPreTagToPreserveWhiteSpace- adds a span tag around the HTML
     *                  so that whitespace is preserved.  If this is false and you want
     *                  whitespace to be preserved, set the style: white-space: pre
     *                  on an element.
     * @returns {string} a new string after escaping html and without escaping links
     *                  and strong tags
     */
    treatHtmlLikeCommandWindow: function (string, escapeLinks, options) {
      if (typeof string !== 'string') {
        return '';
      }
      var newString = '';
      var normalStrings = string.split(this.HYPERLINK_ESCAPE_REGEXP);
      var aTags = string.match(this.HYPERLINK_ESCAPE_REGEXP);
      var newHyperLink = '';
      var that = this;
      var i = 0;
      options = options || {};
      var regexpReplaceFunction = function (match, linkUrl, remainder, linkLabel) {
        var protocol = that.parseUrl(linkUrl).protocol;
        var addBold = '';
        // Only allow linkUrl that has designated protocol,
        // otherwise replace the url with "#"
        if (protocol !== 'http:' &&
                    protocol !== 'https:' &&
                    protocol !== 'matlab:') {
          linkUrl = '#';
        }
        linkUrl = that.escapeHtml(linkUrl);
        linkLabel = that._strongNotEscaped(linkLabel, false);
        if (that.BOLD_LINK_ATTRIBUTE_REGEX.test(remainder)) {
          addBold = ' style="font-weight:bold"';
        }
        newHyperLink = '<a href="' + linkUrl + '"' + addBold + '>' + linkLabel + '</a>';
      };

      if (!escapeLinks) {
        if (normalStrings[0] !== '') {
          newString = this._strongNotEscaped(normalStrings[0], false);
        }

        if (aTags !== null && aTags.length !== 0) {
          for (i = 0; i < aTags.length; i = i + 1) {
            aTags[i].replace(this.HYPERLINK_WITH_PARENTHESIS_ESCAPE_REGEXP,
              regexpReplaceFunction);
            if (normalStrings[i + 1] !== '') {
              normalStrings[i + 1] = this._strongNotEscaped(normalStrings[i + 1],
                false);
            }

            newString += newHyperLink + normalStrings[i + 1];
          }
        }
      } else {
        newString = this.escapeHtml(string);
      }

      if (options.addPreTagToPreserveWhiteSpace) {
        newString = '<span style="white-space: pre">' + newString + '</span>';
      }
      return newString;
    },

    /**
     * This function prevents the strong tag from being escaped out.
     *
     * @private
     *
     * @param {string} string - the string to perform escaping on
     * @param {boolean} escapeWhitespace - call the deprecated escapeWhitespace function
     *                              this is not needed if you are using the pre style.
     * @returns {string} a new string escaping HTML but not whitespace
     */
    _strongNotEscaped: function (string, escapeWhitespace) {
      var newString = '';
      var strongMatches = string.match(this.STRONG_ESCAPE_REGEXP);
      var strongSplits = string.split(this.STRONG_ESCAPE_REGEXP);
      var whiteSpaceEscapeFunction = function noOp (s) {
        return s;
      };
      if (escapeWhitespace) {
        whiteSpaceEscapeFunction = this.escapeWhitespace;
      }

      var i = 0;
      if (strongMatches !== null && strongMatches.length !== 0) {
        newString = whiteSpaceEscapeFunction(this.escapeHtml(strongSplits[0]));
        for (i = 0; i < strongMatches.length; i = i + 1) {
          newString += strongMatches[i] + whiteSpaceEscapeFunction(this.escapeHtml(
            strongSplits[i + 1]));
        }
      } else {
        newString = whiteSpaceEscapeFunction(this.escapeHtml(string));
      }

      return newString;
    },

    /**
     * This function escapes multiple spaces and "\n" with
     * &nbsp; and <br>, however it is far better to use
     * &lt;span style="white-space: pre"&gt;...&lt;/span&gt;
     * To perform this work, as it will properly allow the browser
     * to show all whitepace including "\t" properly, and should be
     * faster than using Regexs, while still preserving the other HTML
     * elements in your code.
     * @deprecated use &lt;span style="white-space: pre"&gt;...&lt;/span&gt;
     * @param {string} string - the string to perform escaping on
     */
    escapeWhitespace: function (string) {
      deprecation.deprecated({
        deprecated: {
          module: 'mw-html-utils/HtmlUtils',
          method: 'escapeWhitespace'
        },
        replacement: {
          module: 'Plain HTML',
          method: 'Set class as white-space: pre' +
                    ' or use <span style="white-space: pre">...</span>'
        }});
      string = string || '';
      return string.replace(/\n/g, '<br>').replace(/ {2}/g, '&nbsp;&nbsp;');
    },

    /**
     * This function handles anchor tags. If the link is MATLAB help, the callback function
     * is invoked. A call back function is responsible for the special handling for helpLinks.
     * For example, the commmandModule and VariableEditorModule pass the command to
     * executionService.
     * The executionService intercepts the request and opens the help documentation in a
     * separate window.
     * @param {function} helpCallback
     * @param {event} event
     */
    handleAnchors: function (helpCallback, event) {
      // Check if href attribute is available
      if (event.target.href) {
        event.preventDefault();
        event.stopPropagation();
        var href = event.target.href;

        if (href.indexOf('matlab:') !== -1) {
          var sub = href.substring(0, 7);
          if (sub === 'matlab:') {
            var command = event.target.href.substring(7);
            helpCallback(window.unescape(command));
          }
        } else {
          window.open(event.target.href);
        }
      }
    },

    /**
     * Checks if DOM element content is overflown
     * @param element
     * @returns {boolean}
     */
    isElementOverflown: function (element) {
      return (element.scrollHeight > element.clientHeight) ||
                (element.scrollWidth > element.clientWidth);
    },

    /**
     * Takes in an HTML String, returns an HTML string
     * without any JavaScript executable code. But still
     * allows the matlab: protocol.  This particular function
     * is designed for sanitization for HTML that would normally
     * be consumed by Java Swing for display purposes.
     * @param {string} dirtyHtml An string containing unsafe html
     * @returns {documentFragment} a document fragment sanitized from dirtyHtml
     */
    sanitizeHtmlFromJava: sanitize.sanitizeHtmlFromJava
  };
});
